import type { SfJQuery } from '@app/types'
import { Controller } from '@hotwired/stimulus'
import DOMPurify from 'dompurify'

export default class extends Controller {
  static targets = ['reassignForm', 'reassignBtn', 'userSelect']
  static values = {
    initialUser: String,
  }

  declare initialUserValue: string

  declare readonly hasUserSelectTarget: boolean
  declare readonly hasReassignFormTarget: boolean
  declare readonly hasReassignBtnTarget: boolean
  readonly reassignFormTarget!: HTMLElement
  readonly reassignBtnTarget!: HTMLElement
  private userSelectTarget!: HTMLSelectElement
  private select2Instance: SfJQuery | null = null
  private pendingReassignConfirm: boolean = false

  private performReassignCheckListener!: () => void

  connect(): void {
    this.initializeSelect2()

    this.performReassignCheckListener = this.submitReassignForm.bind(this)

    window.eventHub.$on(
      'confirm-reassign-check',
      this.performReassignCheckListener
    )
  }

  disconnect(): void {
    this.select2Instance?.off()

    window.eventHub.$off(
      'confirm-reassign-check',
      this.performReassignCheckListener
    )
  }

  showReassignForm(event: Event): void {
    event.preventDefault()

    this.reassignFormTarget.classList.add('tw-inline-block')
    this.reassignFormTarget.classList.remove('tw-hidden')

    this.select2Instance?.select2?.('open')

    // keep the reassign button visible, even if the mouse is no longer hovering on the row (i.e. going into the dropdown)
    this.reassignBtnTarget.classList.remove('tw-opacity-0')
  }

  private hideReassignForm(): void {
    if (!this.reassignBtnTarget || !this.reassignFormTarget) return

    this.reassignFormTarget.classList.remove('tw-inline-block')
    this.reassignFormTarget.classList.add('tw-hidden')

    this.reassignBtnTarget.classList.add('tw-opacity-0')
  }

  private initializeSelect2(): void {
    if (!this.hasUserSelectTarget) {
      return
    }

    this.select2Instance = $(this.userSelectTarget)

    this.select2Instance.on('change', (e: JQuery.ChangeEvent) => {
      const target = e.target as HTMLSelectElement
      const text = DOMPurify.sanitize(
        target.options[target.selectedIndex].innerText,
        { USE_PROFILES: { html: false }, KEEP_CONTENT: false }
      )

      window.modalCitizen.openModalPortal('confirm-reassign-check-modal', {
        initialName: this.initialUserValue,
        newName: text,
      })
      this.pendingReassignConfirm = true
    })

    this.select2Instance.on('select2:close', () => {
      this.hideReassignForm()
    })
  }

  private submitReassignForm(): void {
    if (!this.pendingReassignConfirm) return

    this.pendingReassignConfirm = false

    const form = this.reassignFormTarget.querySelector(
      'form'
    ) as HTMLFormElement
    if (form) {
      form.submit()
    }

    window.Silverfin.Spinner.start()
  }
}
